import {
  ColumnsPlusRight,
  CreditCard,
  Lock,
  LockOpen,
  TipJar,
  Warning,
  X,
} from '@phosphor-icons/react';
import { DotsThreeVertical } from '@phosphor-icons/react/dist/ssr';
import { ClipLoader } from 'react-spinners';
import { DebitCardWithTruck } from '../../../../../../../types/apiTypes';
import { formatNumberCommas } from '../../../../../../../utils/CurrencyUtils';
import CardStatusBadge from './CardStatusBadge';
import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { DemoDataWrapper } from '../../../home/components/DemoDataBadge';

interface CardTableProps {
  cards: DebitCardWithTruck[];
  loadingCards: boolean;
  loadingAccountInfo: boolean;
  currCard: DebitCardWithTruck | null;
  currCardIdx: number;
  screenView: string;
  isDriver?: boolean;
  activePopup: string | null;
  popupRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  togglePopup: (debit_card_id: string | undefined) => void;
  freezeCard: (debit_card_id: string, frozen: boolean) => Promise<void>;
  cancelCard: (debit_card_id: string, last_four_digits: string) => void;
  handleEditSpendLimit: (card: DebitCardWithTruck) => void;
  handleEditSpendCategories: (card: DebitCardWithTruck) => void;
  setActivateCardPopupVisible: (visible: boolean) => void;
  handleOpenCategoriesPopup: (
    e: React.MouseEvent<HTMLDivElement>,
    categories: string[]
  ) => void;
  handleCardItemClick?: (card: DebitCardWithTruck, index: number) => void;
  isPreActive: boolean;
}

const CardTable = ({
  cards,
  loadingCards,
  loadingAccountInfo,
  currCard,
  currCardIdx,
  screenView,
  activePopup,
  isDriver,
  popupRefs,
  togglePopup,
  freezeCard,
  cancelCard,
  handleEditSpendLimit,
  handleEditSpendCategories,
  setActivateCardPopupVisible,
  handleOpenCategoriesPopup,
  handleCardItemClick,
  isPreActive,
}: CardTableProps) => {
  const isLoading = loadingCards || loadingAccountInfo;
  const [buttonPositions, setButtonPositions] = useState<{
    [key: string]: { top: number; left: number; width: number; height: number };
  }>({});

  // Update button position when activePopup changes
  useEffect(() => {
    if (activePopup) {
      const buttonElement = document.getElementById(
        `popup-button-${activePopup}`
      );
      if (buttonElement) {
        const rect = buttonElement.getBoundingClientRect();
        setButtonPositions((prev) => ({
          ...prev,
          [activePopup]: {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          },
        }));
      }
    }
  }, [activePopup]);

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      <div className="flex h-full w-full flex-col overflow-hidden rounded-[4px] border-[1px] border-bordergray bg-white">
        {!isLoading ? (
          <>
            {cards && cards?.length > 0 ? (
              <div className="w-full overflow-x-auto">
                <table className="h-full w-full min-w-[1146px] bg-white">
                  <thead className="h-[50px] max-h-[50px] w-full bg-primarybg">
                    <tr className="h-[50px] max-h-[50px] overflow-hidden">
                      <th className="h-[50px] max-h-[50px] overflow-hidden px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary md:px-4">
                        Card
                      </th>
                      <th className="h-[50px] max-h-[50px] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary md:px-4">
                        Truck Name
                      </th>
                      <th className="h-[50px] max-h-[50px] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary md:px-4">
                        Status
                      </th>
                      <th className="h-[50px] max-h-[50px] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary md:px-4">
                        Spend Limit (Daily / 30-Day)
                      </th>
                      <th className="h-[50px] max-h-[50px] w-[25%] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary md:px-4">
                        Banned Spend Categories
                      </th>
                      <th className="h-[50px] max-h-[50px] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary"></th>
                    </tr>
                  </thead>
                  <tbody className="h-full w-full bg-white">
                    {cards.map((card: DebitCardWithTruck, index: number) => (
                      <tr
                        key={index}
                        className={`h-[50px] max-h-[50px] border-b border-gray-200 ${
                          currCard?.debit_card_id === card?.debit_card_id ||
                          currCardIdx === index
                            ? 'bg-bordergray'
                            : ''
                        } cursor-pointer hover:bg-bordergray`}
                        onClick={() => {
                          handleCardItemClick?.(card, index);
                        }}
                      >
                        <td className="h-[50px] max-h-[50px] px-2 py-2 text-[14px] text-primary md:px-4">
                          **** {card?.last_four_digits}
                        </td>
                        <td className="h-[50px] max-h-[50px] px-2 py-2 text-[14px] text-primary md:px-4">
                          {card?.truck?.truck_display_name ?? 'Business'}
                        </td>

                        <td
                          className={`h-[50px] max-h-[50px] px-2 py-2 text-[14px] md:px-4`}
                        >
                          {!card?.is_cancelled &&
                          card?.physical_card_activated_timestamp === null ? (
                            <div className="relative">
                              <CardStatusBadge card={card} />
                              <div className="absolute right-8 top-[50%] -translate-y-1/2 md:right-4">
                                <Warning size={24} className="text-[#E2660C]" />
                              </div>
                            </div>
                          ) : (
                            <CardStatusBadge card={card} />
                          )}
                        </td>
                        {!card?.is_cancelled ? (
                          <td className="h-[50px] max-h-[50px] px-2 py-2 text-[14px] text-primary md:px-4">
                            ${formatNumberCommas(card?.daily_spend_limit ?? 0)}{' '}
                            / $
                            {formatNumberCommas(
                              card?.thirty_day_spend_limit ?? 0
                            )}
                          </td>
                        ) : (
                          <td className="h-[50px] max-h-[50px] px-2 py-2 text-[14px] text-primary md:px-4">
                            -
                          </td>
                        )}
                        {!card?.is_cancelled ? (
                          <td className="h-[50px] max-h-[50px] w-[30%] px-2 py-2 text-[14px] text-primary md:px-4">
                            <div className="relative flex flex-row gap-1">
                              {card?.banned_spend_categories
                                ?.slice(0, 1)
                                ?.map((category, idx) => (
                                  <div
                                    key={idx}
                                    className="rounded-[20px] border-[1px] border-bordergray bg-white px-3 py-1"
                                  >
                                    {category}
                                  </div>
                                ))}
                              {card?.banned_spend_categories?.length > 1 && (
                                <div
                                  className="cursor-pointer text-nowrap rounded-[20px] border-[1px] border-bordergray bg-white px-3 py-1"
                                  onMouseEnter={(e) =>
                                    handleOpenCategoriesPopup(
                                      e,
                                      card?.banned_spend_categories || []
                                    )
                                  }
                                >
                                  +{card?.banned_spend_categories?.length - 1}{' '}
                                  more
                                </div>
                              )}
                            </div>
                          </td>
                        ) : (
                          <td className="h-[50px] max-h-[50px] w-[25%] px-2 py-2 text-[14px] text-primary md:px-4">
                            -
                          </td>
                        )}
                        <td className="h-[50px] max-h-[50px] px-2 py-2 text-[14px] text-primary md:px-4">
                          {!card?.is_cancelled && !isDriver && (
                            <button
                              id={`popup-button-${card.debit_card_id}`}
                              onClick={(e: any) => {
                                e.stopPropagation();
                                togglePopup(card.debit_card_id);
                              }}
                              className="relative"
                            >
                              <DotsThreeVertical size={24} weight="bold" />
                              {activePopup === card.debit_card_id &&
                                createPortal(
                                  <div
                                    ref={(el) => {
                                      popupRefs.current[
                                        card.debit_card_id ?? ''
                                      ] = el;
                                    }}
                                    className={`fixed z-40 w-[200px] rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl ${isPreActive && 'opacity-80'}`}
                                    style={{
                                      position: 'absolute',
                                      top: `calc(${buttonPositions[card.debit_card_id]?.top || 0}px - 200px)`,
                                      left: `${buttonPositions[card.debit_card_id]?.left || 0}px`,
                                      transform: 'translate(-80%, 0)',
                                    }}
                                  >
                                    <div>
                                      {!card.is_cancelled &&
                                        card?.physical_card_activated_timestamp ===
                                          null && (
                                          <button
                                            disabled={isPreActive}
                                            onClick={(e: any) => {
                                              e.stopPropagation();
                                              setActivateCardPopupVisible(true);
                                            }}
                                            className="flex w-full flex-row items-center space-x-1 rounded-lg px-4 py-2 text-left align-middle text-orange-600 hover:bg-gray-100 disabled:cursor-not-allowed"
                                          >
                                            <img
                                              alt="card-add"
                                              src="/card-tick.svg"
                                              className="h-[20px] w-[20px]"
                                              style={{
                                                filter:
                                                  'invert(30%) sepia(100%) saturate(800%) hue-rotate(359deg) brightness(97%) contrast(101%)',
                                              }}
                                            />
                                            <p>Activate Physical Card</p>
                                          </button>
                                        )}
                                      <button
                                        disabled={isPreActive}
                                        onClick={async (e: any) => {
                                          e.stopPropagation();
                                          handleEditSpendLimit(card);
                                        }}
                                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100 disabled:cursor-not-allowed"
                                      >
                                        <TipJar size={20} />
                                        <p>Edit Spend Limit</p>
                                      </button>
                                      <button
                                        disabled={isPreActive}
                                        onClick={async (e: any) => {
                                          e.stopPropagation();
                                          handleEditSpendCategories(card);
                                        }}
                                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100 disabled:cursor-not-allowed"
                                      >
                                        <ColumnsPlusRight size={20} />
                                        <p>Edit Spend Categories</p>
                                      </button>
                                      {!card.is_cancelled && !card.frozen && (
                                        <button
                                          disabled={isPreActive}
                                          onClick={async (e: any) => {
                                            e.stopPropagation();
                                            await freezeCard(
                                              card.debit_card_id ?? '',
                                              true
                                            );
                                            togglePopup(card.debit_card_id);
                                          }}
                                          className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100 disabled:cursor-not-allowed"
                                        >
                                          <Lock size={20} />
                                          <p>Freeze Card</p>
                                        </button>
                                      )}
                                      {!card.is_cancelled && card.frozen && (
                                        <button
                                          disabled={isPreActive}
                                          onClick={async (e: any) => {
                                            e.stopPropagation();
                                            await freezeCard(
                                              card.debit_card_id ?? '',
                                              false
                                            );
                                            togglePopup(card.debit_card_id);
                                          }}
                                          className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100 disabled:cursor-not-allowed"
                                        >
                                          <LockOpen size={20} />
                                          <p>Unfreeze Card</p>
                                        </button>
                                      )}
                                      {!card.is_cancelled && (
                                        <button
                                          disabled={isPreActive}
                                          onClick={async (e: any) => {
                                            e.stopPropagation();
                                            await cancelCard(
                                              card.debit_card_id ?? '',
                                              card?.last_four_digits ?? ''
                                            );
                                            togglePopup(card.debit_card_id);
                                          }}
                                          className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-danger hover:bg-gray-100 disabled:cursor-not-allowed"
                                        >
                                          <X size={20} />
                                          <p>Cancel Card</p>
                                        </button>
                                      )}
                                    </div>
                                  </div>,
                                  document.body
                                )}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex h-[500px] w-full flex-col items-center justify-center space-y-2">
                <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                  <CreditCard size={40} className="text-graysecondary" />
                </div>
                <p className="text-[14px] leading-[20px] text-graysecondary">
                  {isDriver ? 'No card assigned to you' : 'No cards found'}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full min-h-[40vh] w-full items-center justify-center">
            <ClipLoader
              color="#1D213B"
              loading={isLoading}
              size={60}
              aria-label="Loading Spinner"
              data-testid="loader"
              className="flex h-full w-full items-center justify-center"
            />
          </div>
        )}
      </div>
    </DemoDataWrapper>
  );
};

export default CardTable;
